import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import ProjectCard from './ProjectCard';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { calculatePlanningMetrics } from '../../../services/planningFinance';

const KanbanView = ({ projects, property, properties, onEdit, onDelete, onStatusChange, onCreateProject }) => {
  const { formatCurrency } = useCurrency();
  const statuses = [
    { 
      id: 'planned', 
      label: 'Planifié', 
      color: '#3b82f6',
      icon: 'Calendar'
    },
    { 
      id: 'in_progress', 
      label: 'En cours', 
      color: '#f97316',
      icon: 'Wrench'
    },
    { 
      id: 'on_hold', 
      label: 'En attente', 
      color: 'var(--color-warning)',
      icon: 'Pause'
    },
    { 
      id: 'completed', 
      label: 'Terminé', 
      color: '#22c55e',
      icon: 'CheckCircle'
    }
  ];

  const getProjectsByStatus = (statusId) => {
    return projects?.filter(p => (p?.status || 'planned') === statusId) || [];
  };

  // Calculate totals per status (value creation + average real yield or recovery rate)
  const calculateStatusTotals = useMemo(() => {
    const totals = {};
    
    statuses.forEach(status => {
      const statusProjects = getProjectsByStatus(status.id);
      let totalValueCreation = 0;
      let totalRealYield = 0;
      let realYieldCount = 0;
      let totalRecoveryRate = 0;
      let recoveryCount = 0;
      
      statusProjects.forEach(project => {
        const projectProperty = project.propertyId 
          ? (properties?.find(p => p.id === project.propertyId) || property)
          : property;
        const useActual = project.status === 'completed' && project.actual_cost > 0;
        const metrics = calculatePlanningMetrics(project, projectProperty, { useActualCost: useActual });
        
        totalValueCreation += metrics?.valueCreation ?? 0;
        
        if (metrics?.realYield != null && metrics.realYield > 0) {
          totalRealYield += metrics.realYield;
          realYieldCount++;
        }
        if (metrics?.recoveryRate != null && metrics.recoveryRate > 0) {
          totalRecoveryRate += metrics.recoveryRate;
          recoveryCount++;
        }
      });
      
      totals[status.id] = {
        totalValueCreation,
        avgRealYieldPercent: realYieldCount > 0 ? (totalRealYield / realYieldCount) * 100 : null,
        avgRecoveryRatePercent: recoveryCount > 0 ? (totalRecoveryRate / recoveryCount) * 100 : null,
        count: statusProjects.length
      };
    });
    
    return totals;
  }, [projects, property, properties]);

  return (
    <div style={{
      display: 'flex',
      gap: 'var(--spacing-md)',
      overflowX: 'auto',
      paddingBottom: 'var(--spacing-md)',
      minHeight: '600px'
    }}
    className="kanban-container"
    >
      {statuses.map(status => {
        const statusProjects = getProjectsByStatus(status.id);
        return (
          <div
            key={status.id}
            style={{
              minWidth: '320px',
              width: '320px',
              background: 'var(--color-card)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--color-border)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'calc(100vh - 300px)'
            }}
          >
            {/* Column Header */}
            <div style={{
              padding: 'var(--spacing-md)',
              borderBottom: '2px solid var(--color-border)',
              background: `${status.color}10`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--spacing-xs)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-xs)'
                }}>
                  <Icon name={status.icon} size={18} style={{ color: status.color }} />
                  <h3 style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'var(--color-foreground)'
                  }}>
                    {status.label}
                  </h3>
                </div>
                <span style={{
                  fontFamily: 'var(--font-caption)',
                  fontSize: '0.75rem',
                  color: 'var(--color-muted-foreground)',
                  background: 'var(--color-muted)',
                  padding: '2px 8px',
                  borderRadius: 'var(--radius-sm)',
                  fontWeight: 600
                }}>
                  {statusProjects.length}
                </span>
              </div>
              
              {/* Totals KPIs */}
              {statusProjects.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 'var(--spacing-xs)',
                  marginTop: 'var(--spacing-sm)',
                  padding: 'var(--spacing-xs)',
                  background: 'var(--color-card)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--color-border)'
                }}>
                  <div>
                    <p style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '0.625rem',
                      color: 'var(--color-muted-foreground)',
                      margin: 0
                    }}>
                      {status.id === 'completed' ? 'Valeur créée' : 'Valeur planifiée'}
                    </p>
                    <p style={{
                      fontFamily: 'var(--font-data)',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: calculateStatusTotals[status.id]?.totalValueCreation > 0 
                        ? 'var(--color-success)' 
                        : 'var(--color-foreground)',
                      margin: 0
                    }}>
                      {formatCurrency(calculateStatusTotals[status.id]?.totalValueCreation || 0)}
                    </p>
                  </div>
                  <div>
                    <p style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '0.625rem',
                      color: 'var(--color-muted-foreground)',
                      margin: 0
                    }}>
                      {calculateStatusTotals[status.id]?.avgRealYieldPercent != null ? 'Rend. réel moyen' : 'Taux récup. moyen'}
                    </p>
                    <p style={{
                      fontFamily: 'var(--font-data)',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: (calculateStatusTotals[status.id]?.avgRealYieldPercent ?? calculateStatusTotals[status.id]?.avgRecoveryRatePercent ?? 0) > 0 
                        ? 'var(--color-success)' 
                        : 'var(--color-foreground)',
                      margin: 0
                    }}>
                      {(calculateStatusTotals[status.id]?.avgRealYieldPercent ?? calculateStatusTotals[status.id]?.avgRecoveryRatePercent ?? 0).toFixed(1)}%
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Projects List */}
            <div 
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: 'var(--spacing-sm)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-sm)'
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.style.background = `${status.color}10`;
              }}
              onDragLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.style.background = 'transparent';
                const projectId = e.dataTransfer.getData('projectId');
                const currentStatus = e.dataTransfer.getData('currentStatus');
                if (projectId && currentStatus !== status.id && onStatusChange) {
                  // Call the status change handler - React will handle the UI update via state
                  onStatusChange(projectId, status.id);
                }
              }}
            >
              {statusProjects.length === 0 ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'var(--spacing-lg)',
                  color: 'var(--color-muted-foreground)',
                  textAlign: 'center',
                  minHeight: '200px',
                  border: '2px dashed var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  margin: 'var(--spacing-xs)',
                  gap: 'var(--spacing-md)'
                }}>
                  <Icon name="FolderX" size={32} style={{ opacity: 0.3 }} />
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    margin: 0
                  }}>
                    Aucun projet
                  </p>
                  {onCreateProject && (
                    <button
                      onClick={() => onCreateProject(status.id)}
                      style={{
                        padding: 'var(--spacing-sm) var(--spacing-md)',
                        background: 'var(--color-primary)',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--spacing-xs)',
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: 'white',
                        transition: 'all var(--transition-base)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--color-primary)/90';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--color-primary)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <Icon name="Plus" size={16} />
                      <span>Ajouter</span>
                    </button>
                  )}
                </div>
              ) : (
                statusProjects.map(project => (
                  <div
                    key={project.id}
                    data-project-id={project.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('projectId', project.id);
                      e.dataTransfer.setData('currentStatus', project.status || 'planned');
                      e.currentTarget.style.opacity = '0.5';
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    style={{
                      cursor: 'grab'
                    }}
                  >
                    <ProjectCard
                      project={project}
                      property={project.propertyId ? (properties?.find(p => p.id === project.propertyId) || property) : property}
                      onEdit={() => onEdit(project)}
                      onDelete={() => onDelete(project.id)}
                      compact={true}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KanbanView;
